import { lazy } from "react";
import { Navigate } from "react-router-dom";
import Loadable from "../layouts/loader/Loadable";
import AuthGuard from "../components/authGuard/AuthGuard";
import useAuth from "../components/authGuard/useAuth";
import PropTypes from "prop-types";
import ProcessRuns from "../views/ProcessRuns";
import ProcessSelection from "../views/ProcessSelection";
import LatestRuns from "../views/LatestRuns";
import ApplicationLog from "../views/ApplicationLog";
import DataRollCall from "../views/DataRollCall";
import MyActivities from "../views/MyActivities";
import DettaglioSingolaVersione from "../views/DettaglioSingolaVersione";
import DataQualityIssueReport from "../views/DataQualityIssueReport";
import VersionListRollCall from "../views/VersionListRollCall";
import ProductTypeListRollCall from "../views/ProductTypeListRollCall";
import DownloadManager from "../views/DownloadManager";
import VersionLineage from "../views/VersionLineage";
import DataLineage from "../views/DataLineage";
import ExternalRedirect from "../views/ExternalRedirect";
import DataCatalog from "../views/DataCatalog";
import DataCatalogDetail from "../views/DataCatalogDetail";
import LoginCallback from "../views/LoginCallback";
import Atlan from "../views/Atlan";
import DataQualityAdmin from "../views/DataQualityAdmin";
import { useSelector } from "react-redux";

/****Layouts*****/
const HomeLayout = Loadable(lazy(() => import("../layouts/HomeLayout")));
const BlankLayout = Loadable(lazy(() => import("../layouts/BlankLayout")));

//***** Pages ****/
const DataQualityStatus = Loadable(lazy(() => import("../views/DataQualityStatus")));
const ElencoVersioni = Loadable(lazy(() => import("../views/ElencoVersioni")));
const DettaglioVersione = Loadable(lazy(() => import("../views/DettaglioVersione")));

/***** Auth Pages ****/
const Err = Loadable(lazy(() => import("../views/auth/Error")));
const Unauthorized = Loadable(lazy(() => import("../views/auth/Unauthorized")));
const Register = Loadable(lazy(() => import('../views/auth/Register')));
const FormLoginSSO = Loadable(lazy(() => import('../components/forms/form_login/FormLoginSSO')));

const AdminRoute = ({ children }) => {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) return <div>Verificando permessi...</div>;
  if (!isAdmin) return <Navigate to="/auth/unauthorized" replace />;

  return children;
};

const OwnerDelegateRoute = ({ children, adminCheck = false }) => {
  const { isAdmin, isLoading } = useAuth();
  const dataCatalogOwnership = useSelector((state) => state.dataQualityReducer.dataCatalogOwnership || []);

  if (isLoading) return <div>Verificando permessi...</div>;
  
  // If adminCheck is true and user is admin, allow access
  if (adminCheck && isAdmin) {
    return children;
  }  

  // Otherwise, check if user is owner/delegate
  if (dataCatalogOwnership.length === 0) {
    return <Navigate to="/auth/unauthorized" replace />;
  }

  return children;

};

AdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

OwnerDelegateRoute.propTypes = {
  children: PropTypes.node.isRequired,
  adminCheck: PropTypes.bool,
};

/*****Routes******/
const ThemeRoutes = [
  {
    path: "/",
    element: (
      <AuthGuard>
        <HomeLayout />
      </AuthGuard>
    ),
    children: [
      { path: "/", element: <Navigate to="/home" />, breadcrumb: ['Data Quality Status'], paths: ['/home'] },
      { path: "/home", exact: true, element: <DataQualityStatus />, breadcrumb: ['Data Quality Status'], group: "DataQualityStatus", paths: ['/home'] },
      { path: "/home/totem", exact: true, element: <DataQualityStatus />, breadcrumb: ['Data Quality Status'], paths: ['/home/totem'] },

      { path: "/versioni", exact: true, element: <ElencoVersioni />, breadcrumb: ['Data Quality Status', 'Version List'], group: "DataQualityStatus", paths: ['/home', '/versioni'] },
      { path: "/versioni/detail/:versionId", exact: true, element: <DettaglioVersione />, breadcrumb: ['Data Quality Status', 'Version List', 'Data Quality Detail'], group: "DataQualityStatus", paths: ['/home', '/versioni', '/versioni/detail/:versionId'] },
      { path: "versioni/version-lineage/:versionId", exact: true, element: <VersionLineage />, breadcrumb: ['Data Quality Status', 'Version List', 'Version Lineage'], group: "DataQualityStatus", paths: ['/home', '/versioni', '/versioni/version-lineage/:versionId'] },
      { path: "versioni/data-lineage/:productTypeId", exact: true, element: <DataLineage />, breadcrumb: ['Data Quality Status', 'Version List', 'Product Lineage'], group: "DataQualityStatus", paths: ['/home', '/versioni', '/versioni/data-lineage/:typeId'] },
      { path: "/versioni/detail/single-version/:versionId", exact: true, element: <DettaglioSingolaVersione />, breadcrumb: ['Data Quality Status', 'Version List', 'Data Quality Detail', 'Single Version Content'], group: "DataQualityStatus", paths: ['/home', '/versioni', '/versioni/detail/:versionId', '/versioni/detail/single-version/:versionId'] },

      { path: "/process-runs", exact: true, element: <ProcessRuns />, breadcrumb: ['Process Runs'], group: "ProcessRuns", paths: ['/process-runs'] },
      { path: "/process-runs/detail/application-log/:versionId", exact: true, element: <ApplicationLog />, breadcrumb: ['Process Runs', 'Application Log'], group: "ProcessRuns", paths: ['/process-runs', '/process-runs/detail/application-log/:versionId'] },

      {
        path: "/process-selection", exact: true, element: (

          <OwnerDelegateRoute adminCheck={true}><ProcessSelection /></OwnerDelegateRoute>), breadcrumb: ['Process Launcher'], group: "ProcessSelection", paths: ['/process-selection']
      },
      {
        path: "/process-selection/latest-runs/:processId", exact: true, element:
          (<OwnerDelegateRoute><LatestRuns /></OwnerDelegateRoute>)
        , breadcrumb: ['Process Launcher', 'Latest Runs'], group: "ProcessSelection", paths: ['/process-selection', '/process-selection/latest-runs/:processId']
      },

      { path: "/data-roll-call", exact: true, element: <DataRollCall />, breadcrumb: ['SLA Monitoring'], group: "DataRollCall", paths: ['/data-roll-call'] },
      { path: "/data-roll-call/detail/version-list-roll-call", exact: true, element: <VersionListRollCall />, breadcrumb: ['SLA Monitoring', 'Version List SLA'], group: "DataRollCall", paths: ['/data-roll-call', '/data-roll-call/detail/version-list-roll-call'] },
      { path: "/data-roll-call/detail/product-type-list-roll-call", exact: true, element: <ProductTypeListRollCall />, breadcrumb: ['SLA Monitoring', 'Product List SLA'], group: "DataRollCall", paths: ['/data-roll-call', '/data-roll-call/detail/product-type-list-roll-call'] },

      {
        path: "/my-activities", exact: true, element:
          (<OwnerDelegateRoute>
            <MyActivities />
          </OwnerDelegateRoute>)
        , breadcrumb: ['My Activities'], group: "MyActivities", paths: ['/my-activities']
      },

      { path: "/data-quality-issue-report", exact: true, element: <DataQualityIssueReport />, breadcrumb: ['Data Quality Issue Report'], group: "DataQualityIssueReport", paths: ['/data-quality-issue-report'] },

      { path: "/download-manager", exact: true, element: <DownloadManager />, breadcrumb: ['Download Manager'], group: "DownloadManager", paths: ['/download-manager'] },

      { path: "/external-redirect", exact: true, element: <ExternalRedirect />, breadcrumb: ['Dettaglio controllo automatico'], group: "External", paths: ['/external-redirect'] },

      { path: "/data-catalog", exact: true, element: <DataCatalog />, breadcrumb: ['Data Catalog'], group: "DataCatalog", paths: ['/data-catalog'] },
      { path: "/data-catalog/details/:typeId", exact: true, element: <DataCatalogDetail />, breadcrumb: ['Data Catalog', 'Data Product Details'], group: "DataCatalog", paths: ['/data-catalog', '/data-catalog/detail/:id'] },

      { path: "/login-callback", exact: true, element: <LoginCallback />, breadcrumb: ['Data Quality Status'], group: "DataQualityStatus", paths: ['/homeMSAL'] },


      {
        path: "/admin-center",
        exact: true,
        element: (
          <AdminRoute>
            <DataQualityAdmin />
          </AdminRoute>
        ),
        breadcrumb: ['Admin Center'],
        group: "AdminCenter",
        paths: ['/admin-center']
      },

      { path: "*", element: <Navigate to="/auth/404" /> },
    ],
  },
  {
    path: "/auth",
    element: <BlankLayout />,
    children: [
      { path: '404', element: <Err />, breadcrumb: [] },
      { path: 'unauthorized', element: <Unauthorized />, breadcrumb: [] },
      { path: 'register', element: <Register />, breadcrumb: [] },
      { path: 'login', element: <FormLoginSSO />, breadcrumb: [] },
      { path: 'atlan', element: <Atlan />, breadcrumb: [] },
      { path: '*', element: <Navigate to="/auth/404" />, breadcrumb: [] },
    ],
  },
];

export default ThemeRoutes;